import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";
import { saveAs } from "file-saver";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  AlignmentType,
  ImageRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  VerticalAlign,
} from "docx";
import { getPracticeById, getUserById, getPatientById, fetchTemplateById } from "../../api";
export function ExportReport({ reportContent, metaData }) {
  const [practice, setPractice] = useState(null);
  const [author, setAuthor] = useState(null);
  const [patient, setPatient] = useState(null);
  const [template, setTemplate] = useState(null);
  useEffect(() => {
    if (metaData !== null) {
      async function fetchPracticeAuthorPatient() {
        try {
          const pracData = await getPracticeById(metaData.practice);
          setPractice(pracData);
          const authData = await getUserById(metaData.author);
          setAuthor(authData);
          const patData = await getPatientById(metaData.patient);
          setPatient(patData);
          const templateData = await fetchTemplateById(metaData.template);
          setTemplate(templateData);
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      }
      fetchPracticeAuthorPatient();
    }
  }, [metaData]);
  const getImageDimensions = async (imageUrl) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => resolve({ width: img.width, height: img.height });
    });
  };
  const parseChatGPTResponse = (content) => {
    const sections = [];
    const lines = content.split("\n");
    let currentSection = null;
    lines.forEach((line) => {
      if (line.startsWith("### ")) {
        if (currentSection) sections.push(currentSection);
        currentSection = { heading: line.replace("### ", ""), content: [] };
      } else if (currentSection) {
        currentSection.content.push(line);
      }
    });
    if (currentSection) sections.push(currentSection);
    return sections;
  };
  const exportReport = async () => {
   
    const currentDate = new Date().toLocaleDateString();
    let logoArrayBuffer = null;
    let dynamicWidth = 100;
    try {
      const logoResponse = await fetch(practice.logoURL);
      if (!logoResponse.ok) throw new Error("Failed to fetch logo");
      const logoBlob = await logoResponse.blob();
      logoArrayBuffer = await logoBlob.arrayBuffer();
      const { width, height } = await getImageDimensions(practice.logoURL);
      dynamicWidth = Math.round((75 * width) / height); // Adjusted for height of 75
    } catch (error) {
      console.error("Error fetching logo:", error);
    }
    // Header Table to align logo and right-aligned text side by side
    const headerTable = new Table({
      rows: [
        new TableRow({
          children: [
            new TableCell({
              width: { size: 50, type: WidthType.PERCENTAGE },
              verticalAlign: VerticalAlign.BOTTOM, // Align text to the bottom
              children: [
                // Logo Image
                ...(logoArrayBuffer
                  ? [
                      new Paragraph({
                        children: [
                          new ImageRun({
                            data: logoArrayBuffer,
                            transformation: { height: 75, width: dynamicWidth }, // Height set to 75
                          }),
                        ],
                      }),
                    ]
                  : []),
                // Fields below the image
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `Pr No: ${practice.practiceNumber}`,
                      size: 16,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: `VAT: ${practice.vatNumber}`,
                      size: 16,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  children: [
                    new TextRun({
                      text: practice.practiceWebsite,
                      size: 16,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: " ",
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                })
              ],
            }),
            new TableCell({
              width: { size: 50, type: WidthType.PERCENTAGE },
              verticalAlign: VerticalAlign.BOTTOM, // Align text to the bottom
              children: [
                // Right-aligned header text
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: practice.practiceName,
                      bold: true,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: `${practice.practicePhoneNumber}`,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: `${practice.practiceEmail}`,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: `${practice.practiceAddress} ${practice.practiceAddress2}`,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: `${practice.practiceCity}, ${practice.practicePostalCode}`,
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                }),
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({
                      text: " ",
                      size: 20,
                      font: "Calibri",
                    }),
                  ],
                })
              ],
            }),
          ],
        }),
      ],
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        // Hide table borders
        top: { size: 0, color: "FFFFFF" },
        //bottom: { size: 12, color: "000000" }, // ✅ Visible bottom border
        left: { size: 0, color: "FFFFFF" },
        right: { size: 0, color: "FFFFFF" },
        insideVertical: { size: 0, color: "FFFFFF" },
        insideHorizontal: { size: 0, color: "FFFFFF" }, // ✅ Hide horizontal inner borders
      },
      
    });
    const emptyRow = new Paragraph({
      children: [
        new TextRun({
          text: " ", // Add a space to ensure the paragraph renders
          size: 20, // Keep size consistent with the document
          font: "Calibri",
        }),
      ],
    });
    
    // Date row with actual content
    const dateRow = new Paragraph({
      children: [
        new TextRun({
          text: `${currentDate}`,
          size: 20,
          font: "Calibri",
        }),
      ],
      alignment: AlignmentType.RIGHT,
    });
    
    const title = new Paragraph({
      children: [
        new TextRun({
          text: template.templateTitle,
          bold: true,
          size: 36,
          font: "Calibri",
        }),
      ],
      alignment: AlignmentType.CENTER,
      spacing: { before: 200, after: 200 },
    });
    const sectionParagraphs = parseChatGPTResponse(reportContent)
      .map((section) => [
        new Paragraph({
          children: [
            new TextRun({
              text: section.heading,
              bold: true,
              size: 24,
              font: "Calibri",
            }),
          ],
          spacing: { after: 100 },
        }),
        ...section.content.map((line) => {
          if (line.startsWith("**") && line.endsWith("**")) {
            // Bullet list item
            return new Paragraph({
              text: line.replace(/\*\*/g, ""), // Remove the ** markers
              bullet: { level: 0 }, // Creates a bulleted list item
              spacing: { after: 50 },
              style: "ListParagraph", // Apply bullet style
            });
          } else {
            // Normal paragraph
            return new Paragraph({
              children: [
                new TextRun({
                  text: line.trim(),
                  size: 16,
                  font: "Calibri",
                }),
              ],
              spacing: { after: 50 },
            });
          }
        }),
      ])
      .flat();
      const doc = new Document({
        styles: {
          default: {
            document: {
              run: {
                font: "Calibri", // Set default font
              },
            },
          },
        },
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: 600, // 0.5 inches
                  right: 600,
                  bottom: 600,
                  left: 600,
                },
              },
            },
            children: [headerTable, emptyRow, dateRow, title, ...sectionParagraphs],
          },
        ],
      });
      
      const blob = await Packer.toBlob(doc);
      saveAs(blob, "report.docx");    
  };
  return (
    <div className="d-flex justify-content-center">
      <Button
        variant="primary"
        className="w-25 mt-3"
        onClick={exportReport}
       // disabled={!practice || !template || !metaData || !reportContent}
      >
        Export Report
      </Button>
    </div>
  );
}
